import { IdToken } from '@auth0/auth0-react';
import { useUnifiedAuth } from 'components/Common/UnifiedAuthContext';
import * as React from 'react';
import http, { IFetchParams, _fetch } from 'utils/http';
import * as Sentry from '@sentry/react';
import { ProviderPractice } from 'pages/SuperAdmin/types';
import { UserRole } from 'types/NavTreeModel';
import useLocalStorage from 'hooks/use-local-storage';
import { CacheKeys } from 'utils/constants';
import { Designation, EProfessions } from 'types/Entitites';
import { fetchUserMetadata, fetchProviderDetails } from 'pages/Dashboard/services/provider.services';
import { UserMetadataFlags } from 'pages/Dashboard/types';
import { mixpanelEventInstance, mixpanelSessionReplay } from 'pages/Rewards/mixpanelInstances';
import Toast, { ToastProps } from 'components/Common/Toast';
import { useQuery } from 'react-query';
import { ProviderDetailsResponse } from 'pages/Dashboard/types/providers.types';

type UserMap = Record<UserRole, boolean>;

type FetchContextValues = {
  http: typeof http;
  providerId: string;
  updateActiveProvider: (providerId: ProviderPractice) => void;
  superAdminId: string;
  activeProvider: ProviderPractice;
  isSuggestScalesEnabled: boolean;
  isBillingAdmin: boolean;
  userRoles: UserMap;
  isNewVersionEnabled: boolean;
  isSessionRecordingEnabled: boolean;
  isFastAPIEnabled: boolean;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  isHrRefreshEnabled: boolean;
  providerName: string;
  setSelectedProviderId: (id: string) => void;
  setSelectedProviderName: (name: string) => void;
  selectedProviderId: string;
  selectedProviderName: string;
  isDemoUser: boolean;
  setToastProps: (props: ToastProps | null) => void;
  practiceId: string;
  addPatientGoal: string;
  setAddPatientGoal: (goal: string) => void;
};

export const FetchContext = React.createContext<FetchContextValues>({
  http: { ...http },
  providerId: '',
  updateActiveProvider: () => null,
  superAdminId: '',
  activeProvider: {} as ProviderPractice,
  isSuggestScalesEnabled: false,
  isBillingAdmin: false,
  userRoles: {} as UserMap,
  isNewVersionEnabled: false,
  isSessionRecordingEnabled: false,
  isFastAPIEnabled: false,
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => null,
  isHrRefreshEnabled: false,
  providerName: '',
  setSelectedProviderId: (id: string) => null,
  setSelectedProviderName: (name: string) => null,
  selectedProviderId: '',
  selectedProviderName: '',
  isDemoUser: false,
  setToastProps: () => null,
  practiceId: '',
  addPatientGoal: '',
  setAddPatientGoal: () => null,
});

type FetchContextProviderProps = {
  children: React.ReactNode;
};

export function FetchContextProvider({ children }: FetchContextProviderProps) {
  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated } = useUnifiedAuth();
  const [providerId, setProviderId] = React.useState('');
  const [providerName, setProviderName] = React.useState('');
  const [superAdminId, setSuperAdminId] = React.useState('');
  const [activeProvider, setActiveProvider] = React.useState({} as ProviderPractice);
  const [isSuggestScalesEnabled] = React.useState(false);
  const [userRoles, setUserRoles] = React.useState<UserMap>({} as UserMap);
  const [localProvider, setLocalProvider] = useLocalStorage(CacheKeys.providerId);
  const [isNewVersionEnabled, setIsNewVersionEnabled] = React.useState(false);
  const [isSessionRecordingEnabled, setIsSessionRecordingEnabled] = React.useState(false);
  const [isFastAPIEnabled] = React.useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [isHrRefreshEnabled, setIsHrRefreshEnabled] = React.useState(true);
  const [selectedProviderId, setSelectedProviderId] = React.useState<string>('');
  const [selectedProviderName, setSelectedProviderName] = React.useState<string>('');
  const [isDemoUser, setIsDemoUser] = React.useState<boolean>(false);
  const [toastProps, setToastProps] = React.useState<ToastProps | null>(null);
  const [practiceId, setPracticeId] = React.useState('');
  const [addPatientGoal, setAddPatientGoal] = React.useState('');

  const parsedLocalProvider = React.useMemo(
    () => JSON.parse(localProvider ?? '{}'),
    [localProvider],
  );

  React.useEffect(() => {
    if (parsedLocalProvider?.providerId && parsedLocalProvider?.providerId !== providerId) {
      const { providerId: id, providerName: name, providerDesignation } = parsedLocalProvider;
      setProviderId(id);
      setActiveProvider({
        providerId: id,
        providerName: name,
        providerDesignation,
      } as ProviderPractice);
    }
  }, [parsedLocalProvider, providerId]);

  const isBillingAdmin = React.useMemo(
    () => userRoles[UserRole.BILLING_ADMIN]
      || activeProvider.providerDesignation === Designation.PROVIDER_DESIGNATION_BILLING_ADMIN,
    [activeProvider.providerDesignation, userRoles],
  );

  const { loginWithRedirect } = useUnifiedAuth();
  const fetchWithToken = React.useCallback(
    async (url: string, params: IFetchParams) => {
      try {
        const onboardingToken = localStorage.getItem('accessToken');
        let authToken: string | null = onboardingToken;
        if (!authToken) {
          // fallback to Auth0 if onboarding token is not present
          authToken = (await getAccessTokenSilently()) ?? '';
        }
        const headers = (params.headers as Headers) ?? {};
        return await _fetch(url, {
          ...params,
          headers: {
            Authorization: `Bearer ${authToken}`,
            ...headers,
          } as Partial<RequestInit>,
        });
      }
      catch (e: unknown) {
        let message = '';
        let errorCode = '';
        if (typeof e === 'object' && e !== null) {
          const err = e as { error?: string; message?: string };
          message = typeof err.message === 'string' ? err.message : String(e);
          errorCode = err.error ?? '';
        }
        else if (typeof e === 'string') {
          message = e;
        }
        if (
          errorCode === 'login_required'
          || message.includes('login required')
          || message.includes('Failed to validate JWT')
        ) {
          localStorage.removeItem('accessToken');
          loginWithRedirect();
        }
        throw e;
      }
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  const httpModule = React.useMemo(
    () => ({
      delete: async (url: string, params?: IFetchParams) => fetchWithToken(url, {
        method: 'DELETE',
        ...(params ?? {}),
      }),
      get: async (url: string, params?: IFetchParams) => fetchWithToken(url, {
        method: 'GET',
        ...(params ?? {}),
      }),
      patch: async (
        url: string,
        payload: object,
        params?: IFetchParams,
      ) => fetchWithToken(url, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        ...(params ?? {}),
      }),

      post: async (url: string, payload: object, params?: IFetchParams) => fetchWithToken(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        ...(params ?? {}),
      }),

      put: async (
        url: string,
        payload: object,
        params?: IFetchParams,
      ) => fetchWithToken(url, {
        method: 'PUT',
        body: JSON.stringify(payload),
        ...(params ?? {}),
      }),
    }),
    [fetchWithToken],
  );

  const updateActiveProvider = React.useCallback(
    (provider: ProviderPractice) => {
      const { providerId: id, providerName: name, providerDesignation } = provider;
      setLocalProvider(JSON.stringify({ providerId: id, providerName: name, providerDesignation }));
      setProviderId(provider.providerId);
      setActiveProvider(provider);
    },
    [setLocalProvider],
  );

  const getNewVersionFeatureFlag = React.useCallback(async (userId: string) => {
    try {
      const patientScalesEnabledResponse = await fetchUserMetadata(httpModule.post, {
        name: UserMetadataFlags.NewVersionFeatureFlag,
        userId,
      })();
      setIsNewVersionEnabled(patientScalesEnabledResponse.flag?.value === 'true');
    }
    catch (err) {
      setIsNewVersionEnabled(false);
    }
  }, [httpModule.post]);

  const getHrRefreshFeatureFlag = React.useCallback(async (userId: string) => {
    try {
      const hrRefreshEnabledResponse = await fetchUserMetadata(httpModule.post, {
        name: UserMetadataFlags.EnableHrRefresh,
        userId,
      })();
      setIsHrRefreshEnabled(hrRefreshEnabledResponse.flag?.value === 'true');
    }
    catch (err) {
      setIsHrRefreshEnabled(false);
    }
  }, [httpModule.post]);

  const getSessionRecordingFeatureFlag = React.useCallback(async (userId: string) => {
    try {
      const sessionFeatureResponse = await fetchUserMetadata(httpModule.post, {
        name: UserMetadataFlags.EnableSessionRecording,
        userId,
      })();
      setIsSessionRecordingEnabled(sessionFeatureResponse.flag?.value === 'true');
    }
    catch (err) {
      setIsSessionRecordingEnabled(false);
    }
  }, [httpModule.post]);

  // Fetch provider details
  const providerIdDetails = providerId || parsedLocalProvider?.providerId || '';

  const { data: providerDetails } = useQuery(
    ['provider-details', providerIdDetails],
    fetchProviderDetails(httpModule.get),
    { enabled: !!providerIdDetails },
  );

  const getDemoUserFeatureFlag = React.useCallback(async (userId: string) => {
    try {
      const demoUserResponse = await fetchUserMetadata(httpModule.post, {
        name: UserMetadataFlags.UserType,
        userId,
      })();
      const value = demoUserResponse.flag?.value;
      setIsDemoUser(value === 'demo');
    }
    catch (err) {
      setIsDemoUser(false);
    }
  }, [httpModule.post]);

  const handleMixpanelIdentification = React.useCallback((
    userProviderId: string,
    idTokenClaims: IdToken | undefined,
    providerInfo: ProviderDetailsResponse | undefined,
  ) => {
    // Set default properties for all Mixpanel events
    const defaultMixpanelProps = {
      providerId: userProviderId,
      providerName: idTokenClaims?.name,
      roles: idTokenClaims?.['headlamp/roles'] ?? [],
      practiceName: providerInfo?.practiceName ?? '',
      practiceId: providerInfo?.practiceId ?? '',
      providerDesignation: EProfessions[providerInfo?.provider?.designation as keyof typeof EProfessions] ?? '',
      subDesignation: providerInfo?.provider?.designationDesc ?? '',
    };

    mixpanelEventInstance.identify(userProviderId);
    mixpanelEventInstance.register(defaultMixpanelProps);
    mixpanelEventInstance.people.set({
      name: idTokenClaims?.name,
      type: 'provider',
    });

    mixpanelSessionReplay.identify(userProviderId);
    mixpanelSessionReplay.register(defaultMixpanelProps);
    mixpanelSessionReplay.people.set({
      name: idTokenClaims?.name,
      type: 'provider',
    });
  }, []);

  const setProviderIdFromClaims = React.useCallback(async () => {
    const idTokenClaims = await getIdTokenClaims();
    const roles = idTokenClaims && Array.isArray(idTokenClaims['headlamp/roles']) ? idTokenClaims['headlamp/roles'] : [];
    const rolesMap = roles.reduce(
      (acc: UserMap, role: UserRole) => ({ ...acc, [role]: true }),
      {} as UserMap,
    );
    const hasSuperAdminRole = (Array.isArray(roles) && roles.includes(UserRole.SUPER_ADMIN))
    || false;
    const userProviderId = typeof idTokenClaims?.provider_id === 'string' ? idTokenClaims.provider_id : '';
    const selectedUserProviderId = hasSuperAdminRole
      ? (parsedLocalProvider?.providerId ?? userProviderId)
      : userProviderId;
    const selectedUserProviderName = typeof idTokenClaims?.name === 'string' ? idTokenClaims.name : '';
    setUserRoles(rolesMap);
    setProviderId(selectedUserProviderId);
    setProviderName(selectedUserProviderName);
    getNewVersionFeatureFlag(selectedUserProviderId);
    getSessionRecordingFeatureFlag(selectedUserProviderId);
    getHrRefreshFeatureFlag(selectedUserProviderId);
    getDemoUserFeatureFlag(selectedUserProviderId);
    // TODO : Uncomment this when this feature starts working
    // getPatientManifestFeatureFlag(userProviderId);
    setSuperAdminId(hasSuperAdminRole ? userProviderId : '');
    Sentry.setUser({
      email: typeof idTokenClaims?.email === 'string' ? idTokenClaims.email : undefined,
      id: userProviderId,
      device: navigator?.userAgent ?? '',
      deviceHeight: window?.innerHeight ?? '',
      deviceWidth: window?.innerWidth ?? '',
    });

    handleMixpanelIdentification(userProviderId, idTokenClaims, providerDetails);
  }, [
    getIdTokenClaims,
    parsedLocalProvider?.providerId,
    getNewVersionFeatureFlag,
    getSessionRecordingFeatureFlag,
    getHrRefreshFeatureFlag,
    providerDetails,
    getDemoUserFeatureFlag,
    handleMixpanelIdentification,
  ]);

  React.useEffect(() => {
    if (isAuthenticated) {
      setProviderIdFromClaims();
    }
  }, [isAuthenticated, setProviderIdFromClaims]);

  React.useEffect(() => {
    if (providerDetails?.practiceId) {
      setPracticeId(providerDetails.practiceId);
      setAddPatientGoal(providerDetails.provider.addPatientGoal || '');
    }
  }, [
    providerDetails?.practiceId,
    providerDetails?.provider.addPatientGoal,
  ]);

  const ctxValue = React.useMemo(
    () => ({
      http: httpModule,
      providerId,
      activeProvider,
      superAdminId,
      isSuggestScalesEnabled,
      isBillingAdmin,
      userRoles,
      isNewVersionEnabled,
      isSessionRecordingEnabled,
      isFastAPIEnabled,
      hasUnsavedChanges,
      updateActiveProvider,
      setHasUnsavedChanges,
      isHrRefreshEnabled,
      providerName,
      setSelectedProviderId,
      setSelectedProviderName,
      selectedProviderId,
      selectedProviderName,
      isDemoUser,
      setToastProps,
      practiceId,
      addPatientGoal,
      setAddPatientGoal,
    }),
    [
      httpModule,
      providerId,
      activeProvider,
      superAdminId,
      isSuggestScalesEnabled,
      isBillingAdmin,
      userRoles,
      isNewVersionEnabled,
      isSessionRecordingEnabled,
      isFastAPIEnabled,
      hasUnsavedChanges,
      updateActiveProvider,
      setHasUnsavedChanges,
      isHrRefreshEnabled,
      providerName,
      setSelectedProviderId,
      setSelectedProviderName,
      selectedProviderId,
      selectedProviderName,
      isDemoUser,
      setToastProps,
      practiceId,
      addPatientGoal,
      setAddPatientGoal,
    ],
  );

  return (
    <FetchContext.Provider value={ctxValue}>
      {toastProps && (
        <Toast {...toastProps} autoHideDuration={6000} onClose={() => setToastProps(null)} />
      )}
      {children}
    </FetchContext.Provider>
  );
}

export function useHttp() {
  const values = React.useContext(FetchContext);
  if (!values) {
    throw Error('Component is not inside FetchContext Provider');
  }
  return { ...values };
}
